import groupBy from 'lodash/groupBy';
import { PortfolioAllocation, PortfolioAllocationDataPoint } from '@models';
import {
  nestPortfolioDataPoints,
  sortPortfolioDataPoints,
} from '@products/portfolio/portfolio.utils';
import {
  PortfolioGeographicAllocationDTO,
  PortfolioDTO,
  PortfolioComponentId,
} from '@types';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import { getFirstNonEmptyProp, parseStdNumber, parseYN } from '../mapper.utils';
import { Mapper, MapperParams } from '../type.mapper';

export const isGeographicExposure = (
  point: PortfolioGeographicAllocationDTO
): boolean =>
  point.calcnamestd?.endsWith('_NET') || point.calcnamestd?.endsWith('_GROSS');

export const isGeographicAllocation = (
  point: PortfolioGeographicAllocationDTO
): boolean => !isGeographicExposure(point);

// tslint:disable-next-line:max-line-length
// example fund (exposure chart): https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/18120/A/franklin-k-2-alternative-strategies-fund/FAAAX#portfolio
export const mapGeographicExposure = (
  data: PortfolioGeographicAllocationDTO[]
): PortfolioAllocation => {
  const allocation: PortfolioAllocation = {
    componentId: PortfolioComponentId.GEOGRAPHIC_EXPOSURE,
    baseLabelKey: 'geographic-exposure',
    showExposureChart: true,
    allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
    allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
    asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
    calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
    // calculationType: getFirstNonEmptyProp(data, 'calctypelocal'), // TODO: calctypelocal doesn't exist in gql
    hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
    updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
    benchmarkName: getFirstNonEmptyProp(data, 'bmname'),
    data: Object.values(groupBy(data, 'brkdwnname'))
      // after grouping, we'll get an array with [gross, net] pairs e.g. [[{gross}, {net}], [{net}, {gross}], [{gross}, {net}], ...]
      .map(
        (
          group: PortfolioGeographicAllocationDTO[]
        ): PortfolioAllocationDataPoint => {
          // extract the gross and net data points for this category
          const netPoint: PortfolioGeographicAllocationDTO = group.find(
            (point: PortfolioGeographicAllocationDTO): boolean =>
              point.calcnamestd?.endsWith('_NET')
          );
          const grossPoint: PortfolioGeographicAllocationDTO = group.find(
            (point: PortfolioGeographicAllocationDTO): boolean =>
              point.calcnamestd?.endsWith('_GROSS')
          );
          // return a single point with gross+net values
          return {
            label: group[0].brkdwnname,
            grossExposure: formatPercentage(grossPoint?.brkdwnpct),
            grossExposureStd: parseStdNumber(grossPoint?.brkdwnpctstd),
            netExposure: formatPercentage(netPoint?.brkdwnpct),
            netExposureStd: parseStdNumber(netPoint?.brkdwnpctstd),
            // work out this point's order based on the lowest "rank" property of the category
            order: Math.min(
              parseStdNumber(grossPoint?.rank),
              parseStdNumber(netPoint?.rank)
            ),
          };
        }
      )
      // sort the array
      .sort(
        (
          a: PortfolioAllocationDataPoint,
          b: PortfolioAllocationDataPoint
        ): number => a.order - b.order
      ),
  };
  return allocation;
};

/**
 * example fund (nested data):
 * https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/4402/A/franklin-biotechnology-discovery-fund/FBDIX#portfolio
 */
export const mapGeographicAllocation = (
  data: PortfolioGeographicAllocationDTO[]
): PortfolioAllocation => {
  let dataPoints: PortfolioAllocationDataPoint[] = data
    .sort(sortPortfolioDataPoints)
    .map(
      (
        obj: PortfolioGeographicAllocationDTO
      ): PortfolioAllocationDataPoint => ({
        label: obj.brkdwnname,
        parent: obj.parent, // will turn an empty string into null, to keep consistency
        breakdown: formatPercentage(obj.brkdwnpct),
        breakdownStd: parseStdNumber(obj.brkdwnpctstd),
        benchmark: formatPercentage(obj.bmbrkdwnpct),
        benchmarkStd: parseStdNumber(obj.bmbrkdwnpctstd),
      })
    );

  dataPoints = nestPortfolioDataPoints(dataPoints);

  const allocation: PortfolioAllocation = {
    componentId: PortfolioComponentId.GEOGRAPHIC_BREAKDOWN,
    baseLabelKey: 'geographic-allocation',
    allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
    allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
    asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
    calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
    calculationType: getFirstNonEmptyProp(data, 'calcname'),
    hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
    updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
    benchmarkName: getFirstNonEmptyProp(data, 'bmname'),
    data: dataPoints,
  };
  return allocation;
};

export class GeographicAllocationMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioGeographicAllocationDTO[] =
      portfolioDto.countryallocation;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    const allocations: PortfolioAllocation[] = [];

    const geographicExposureData: PortfolioGeographicAllocationDTO[] = data.filter(
      isGeographicExposure
    );
    if (geographicExposureData.length > 0) {
      allocations.push(mapGeographicExposure(geographicExposureData));
    }

    const geographicAllocationData: PortfolioGeographicAllocationDTO[] = data.filter(
      isGeographicAllocation
    );
    if (geographicAllocationData.length > 0) {
      allocations.push(mapGeographicAllocation(geographicAllocationData));
    }

    return allocations;
  }
}
